import { useCallback, useEffect, useRef } from 'react';
import { usePageLayout } from '../PageLayout/Context/PageLayoutContext';

interface IUseMainContent {
  isDetailsOpenned?: boolean;
  timeoutDetailed?: number;
  justChildContent?: boolean;
  is100PercentJustChildContent?: boolean;
}

export const useMainContent = ({
  isDetailsOpenned,
  justChildContent = false,
  timeoutDetailed = 0,
  is100PercentJustChildContent = false,
}: IUseMainContent) => {
  const refWrapperContentDetailed = useRef<HTMLDivElement>(null);
  const refContent = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const { footerText } = usePageLayout();

  const adjustContentHeight = useCallback(() => {
    if (refWrapperContentDetailed.current && refContent.current) {
      if (!is100PercentJustChildContent) {
        const wrapperContentDetailedHeight =
          refWrapperContentDetailed.current.getBoundingClientRect().height;

        refContent.current.style.height = `${wrapperContentDetailedHeight}px`;
      } else {
        refContent.current.style.height = '100%';
      }
    }
  }, [is100PercentJustChildContent]);

  useEffect(() => {
    if (!isDetailsOpenned) {
      if (refContent.current) refContent.current.style.height = '100%';

      // Limpa o ResizeObserver quando não está aberto
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      return;
    }

    // Configura o ResizeObserver para observar mudanças no tamanho
    if (refWrapperContentDetailed.current && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        // Usa requestAnimationFrame para otimizar performance
        requestAnimationFrame(() => {
          adjustContentHeight();
        });
      });

      resizeObserverRef.current.observe(refWrapperContentDetailed.current);
    }

    // Adiciona listener para resize da janela (fallback)
    window.addEventListener('resize', adjustContentHeight);

    // Ajuste inicial com timeout
    setTimeout(() => {
      adjustContentHeight();
    }, timeoutDetailed);

    return () => {
      window.removeEventListener('resize', adjustContentHeight);

      // Limpa o ResizeObserver no cleanup
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [isDetailsOpenned, adjustContentHeight, timeoutDetailed, justChildContent]);

  // Cleanup quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  return {
    refWrapperContentDetailed,
    refContent,
    footerText,
  };
};
